import { useEffect, useState, useCallback, useMemo, type FormEvent } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import { Plus, FolderKanban, X, TrendingUp, Briefcase, AlertTriangle } from "lucide-react";
// PageHeader is still JS — import as any so its runtime prop-typing (all
// required at the destructure) doesn't fight our TS call sites.
import PageHeaderJs from "@/components/PageHeader";
const PageHeader = PageHeaderJs as unknown as React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  testid?: string;
  right?: React.ReactNode;
  accent?: boolean;
}>;
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import JobCard, { type JobLike } from "@/components/jobs/JobCard";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { formatMoney } from "@/shared";

/**
 * Jobs & Budgets — decluttered per iter47.
 *
 * Layout summary:
 *   1. PageHeader                                (compact)
 *   2. 3-stat KPI card: Spend YTD · Jobs · Alerts
 *   3. Compact ATO disclaimer (tax_summary context)
 *   4. + New job button — inline collapse form
 *   5. Job cards (each: name+status, progress, spent/remaining, chips, expandable Manage strip)
 *
 * Nothing from the previous screen was removed — the alert bell, threshold
 * dropdown, Draft invoice, Delete, Site documents, and Add-job form all
 * still exist. They just live inside expandable sections so the default
 * scanning experience stays clean.
 */

interface SummaryShape {
  per_job: Record<string, number>;
  total_spend: number;
}

interface JobFormState {
  name: string;
  client: string;
  address: string;
  budget: number | string;
}

const EMPTY_FORM: JobFormState = { name: "", client: "", address: "", budget: "" };

export default function JobsPage() {
  const { jobs, reloadJobs } = useApp() as unknown as {
    jobs: JobLike[];
    reloadJobs: () => Promise<void>;
  };

  const [summary, setSummary] = useState<SummaryShape>({
    per_job: {},
    total_spend: 0,
  });
  const [form, setForm] = useState<JobFormState>(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [jobAtts, setJobAtts] = useState<Record<string, unknown[]>>({});
  const [defaultThreshold, setDefaultThreshold] = useState(60);
  const [pendingDeleteJob, setPendingDeleteJob] = useState<string | null>(null);

  useEffect(() => {
    api
      .get("/settings/alert-threshold")
      .then(({ data }: { data: { effective: number } }) =>
        setDefaultThreshold(Math.round(data.effective))
      )
      .catch(() => {
        /* keep 60 fallback */
      });
  }, []);

  useEffect(() => {
    const next: Record<string, unknown[]> = {};
    for (const j of jobs) next[j.id] = (j.attachments as unknown[]) || [];
    setJobAtts(next);
  }, [jobs]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get<SummaryShape>("/summary");
      setSummary(data);
    } catch (err) {
      devLog.warn("summary load failed", err);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, jobs.length]);

  const createJob = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/jobs", {
        ...form,
        budget: Number(form.budget) || 0,
      });
      setForm(EMPTY_FORM);
      setShowAdd(false);
      await reloadJobs();
      toast.success("Job created");
    } catch {
      toast.error("Failed");
    }
  };

  const deleteJob = async (id: string): Promise<void> => {
    setPendingDeleteJob(id);
  };
  const confirmDeleteJob = async () => {
    const id = pendingDeleteJob;
    setPendingDeleteJob(null);
    if (!id) return;
    try {
      await api.delete(`/jobs/${id}`);
      await reloadJobs();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  /* KPI derivations — memoised so the card doesn't flicker on unrelated renders. */
  const kpis = useMemo(() => {
    let alerts = 0;
    for (const j of jobs) {
      const budget = Number(j.budget) || 0;
      if (budget <= 0) continue;
      const spent = summary.per_job?.[j.id] ?? 0;
      const eff = (j.threshold_pct as number | null | undefined) ?? defaultThreshold;
      if ((spent / budget) * 100 >= eff) alerts += 1;
    }
    return {
      totalSpend: Number(summary.total_spend || 0),
      jobsCount: jobs.length,
      alertsCount: alerts,
    };
  }, [jobs, summary, defaultThreshold]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FolderKanban}
        title="Jobs & Budgets"
        subtitle={`${kpis.jobsCount} ${kpis.jobsCount === 1 ? "job" : "jobs"} tracked`}
        testid="jobs-header"
      />

      {/* 3-stat KPI card ------------------------------------------------- */}
      <div className="bi-elevate p-5" data-testid="jobs-kpi-card">
        <div className="grid grid-cols-[1.6fr_1fr_1fr] gap-4 items-end">
          <div>
            <div className="flex items-center gap-1.5 text-[#FF9500] mb-1">
              <TrendingUp className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-heading font-bold inline-flex items-baseline gap-[0.35em]">
                <span>Spend</span><span>YTD</span>
              </span>
            </div>
            <div
              data-testid="jobs-kpi-total-spend"
              className="font-heading text-[30px] sm:text-[34px] font-extrabold tabular-nums leading-none"
            >
              {formatMoney(kpis.totalSpend)}
            </div>
          </div>
          <div className="border-l border-white/[0.06] pl-4">
            <div className="flex items-center gap-1.5 text-white/45 mb-1">
              <Briefcase className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-heading font-bold">
                Jobs
              </span>
            </div>
            <div
              data-testid="jobs-kpi-count"
              className="font-heading text-2xl font-extrabold tabular-nums leading-none"
            >
              {kpis.jobsCount}
            </div>
          </div>
          <div className="border-l border-white/[0.06] pl-4">
            <div
              className={`flex items-center gap-1.5 mb-1 ${
                kpis.alertsCount > 0 ? "text-[#FBBF24]" : "text-white/45"
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-heading font-bold">
                Alerts
              </span>
            </div>
            <div
              data-testid="jobs-kpi-alerts"
              className={`font-heading text-2xl font-extrabold tabular-nums leading-none ${
                kpis.alertsCount > 0 ? "text-[#FBBF24]" : "text-white"
              }`}
            >
              {kpis.alertsCount}
            </div>
          </div>
        </div>
      </div>

      {/* Compact disclaimer — this screen shows spend numbers that flow into BAS */}
      <AtoDisclaimer context="tax_summary" compact testId="jobs-disclaimer" />

      {/* + New job — collapsed by default so the fold isn't crowded ------ */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-heading font-bold">
          Active jobs
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          data-testid="jobs-add-toggle"
          aria-expanded={showAdd}
          className={`h-9 px-3 rounded-full text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition ${
            showAdd
              ? "bg-white/[0.08] text-white"
              : "bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30"
          }`}
        >
          {showAdd ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {showAdd ? "Cancel" : "New job"}
        </button>
      </div>

      {showAdd && (
        <form
          onSubmit={createJob}
          data-testid="jobs-add-form"
          className="bi-surface p-4 space-y-3 bi-rise"
        >
          <input
            data-testid="job-form-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bi-input"
            placeholder="Job name"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="job-form-client"
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
              className="bi-input"
              placeholder="Client"
            />
            <input
              data-testid="job-form-budget"
              type="number"
              inputMode="decimal"
              min="0"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              className="bi-input"
              placeholder="Budget (optional)"
            />
          </div>
          <input
            data-testid="job-form-address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="bi-input"
            placeholder="Site address (optional)"
          />
          <button
            data-testid="job-form-submit"
            type="submit"
            className="w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wider text-sm"
          >
            <Plus className="w-4 h-4" /> Add job
          </button>
        </form>
      )}

      {/* Job cards ------------------------------------------------------- */}
      <div className="space-y-2.5">
        {jobs.map((j) => (
          <JobCard
            key={j.id}
            job={j}
            spent={summary.per_job?.[j.id] || 0}
            defaultThreshold={defaultThreshold}
            attachments={jobAtts[j.id] || []}
            onAttachmentsChange={(next) =>
              setJobAtts((prev) => ({ ...prev, [j.id]: next }))
            }
            onReload={reloadJobs}
            onDelete={deleteJob}
          />
        ))}
        {jobs.length === 0 && (
          <div
            data-testid="jobs-empty-state"
            className="bi-surface p-8 text-center text-sm text-white/50"
          >
            No jobs yet — tap <b className="text-[#FF9500]">New job</b> above to create your first one.
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!pendingDeleteJob}
        title="Delete this job?"
        description="Entries logged against this job will become 'Unassigned'. This can't be undone."
        onConfirm={confirmDeleteJob}
        onCancel={() => setPendingDeleteJob(null)}
        testId="job-delete"
      />
    </div>
  );
}
